<template>
  <div class="page">
    <main class="content">
      <section class="commands-section">
        <!-- Header area with title and search -->
        <div class="commands-header">
          <h1 class="commands-title">Commands</h1>
          <p class="commands-subtitle">Explore all the powerful features Partycord has to offer</p>
          
          <!-- Search box -->
          <div class="search-container">
            <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input 
              v-model="searchQuery" 
              type="text" 
              class="search-input" 
              placeholder="Search commands..."
              @focus="searchFocused = true"
              @blur="searchFocused = false"
            />
            <button v-if="searchQuery" class="search-clear" @click="searchQuery = ''">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Category pills -->
        <div class="category-buttons">
          <button 
            v-for="(categoryCommands, category) in groupedCommands" 
            :key="category" 
            :class="{ 'selected': selectedCategory === category }" 
            @click="selectedCategory = category"
            class="category-button"
          >
            <span class="category-icon">{{ getCategoryIcon(category) }}</span>
            <span class="category-name">{{ category }}</span>
            <span class="category-count">{{ categoryCommands.length }}</span>
          </button>
        </div>

        <!-- Results info -->
        <div class="results-info" v-if="searchQuery">
          <span class="results-count">{{ filteredCommands.length }} result{{ filteredCommands.length !== 1 ? 's' : '' }}</span>
          <span class="results-query">for "{{ searchQuery }}"</span>
        </div>

        <!-- Commands grid -->
        <div class="commands-grid" v-if="selectedCategory || searchQuery">
          <div 
            v-for="command in filteredCommands" 
            :key="command.name" 
            class="command-card" 
            @click="selectedCommand = command" 
            :class="{ 'hovered': command === hoveredCommand }" 
            @mouseover="hoveredCommand = command" 
            @mouseleave="hoveredCommand = null"
          >
            <div class="card-glow"></div>
            <div class="card-content">
              <div class="command-header">
                <h2 class="command-name">/{{ command.name }}</h2>
                <span class="command-category-tag" v-if="searchQuery">{{ command.category }}</span>
              </div>
              <p class="command-description">{{ command.description }}</p>
              <div class="command-footer">
                <span class="view-details">
                  View details
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- No results message -->
        <div class="no-results" v-if="searchQuery && filteredCommands.length === 0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
            <path d="M8 8l6 6"></path>
            <path d="M14 8l-6 6"></path>
          </svg>
          <p>No commands found matching "{{ searchQuery }}"</p>
          <button class="clear-search-btn" @click="searchQuery = ''">Clear search</button>
        </div>
      </section>
    </main>
    <command-modal v-if="selectedCommand" :command="selectedCommand" @close="selectedCommand = null"></command-modal>
  </div>
</template>

<script>
import CommandModal from '../components/CommandModal.vue';
import commands from '../data/commands.json';

export default {
  components: {
    CommandModal
  },
  data() {
    return {
      commands: [],
      selectedCategory: null,
      selectedCommand: null,
      hoveredCommand: null,
      searchQuery: '',
      searchFocused: false,
      categoryIcons: {
        'Admin': '🔰',
        'General': '⚡',
        'Settings': '⚙️',
        'Channel': '🔊',
        'User': '🎭',
        'Text': '💬',
        'Premium': '💎',
        'Fun': '🎮',
        'Music': '🎵',
        'Moderation': '🔒',
        'Utility': '🔧',
        'Patreon': '🎉',
        'Info': '❓',
      }
    };
  },
  computed: {
    groupedCommands() {
      const grouped = {};
      this.commands.forEach(command => {
        if (command.category != 'Developer') {
          if (!grouped[command.category]) {
            grouped[command.category] = [];
          }
          grouped[command.category].push(command);
        }
      });
      return grouped;
    },
    filteredCommands() {
      let commandsToFilter = [];
      
      if (this.searchQuery) {
        // When searching, search across all commands
        commandsToFilter = this.commands.filter(cmd => cmd.category !== 'Developer');
      } else if (this.selectedCategory) {
        // When not searching, use selected category
        commandsToFilter = this.groupedCommands[this.selectedCategory] || [];
      }
      
      if (!this.searchQuery) {
        return commandsToFilter;
      }
      
      const query = this.searchQuery.toLowerCase().trim();
      return commandsToFilter.filter(command => 
        command.name.toLowerCase().includes(query) ||
        command.description.toLowerCase().includes(query) ||
        command.category.toLowerCase().includes(query)
      );
    }
  },
  methods: {
    getCommands() {
      this.commands = commands;
      this.selectedCategory = Object.keys(this.groupedCommands)[0];
    },
    getCategoryIcon(category) {
      return this.categoryIcons[category] || '📋';
    }
  },
  mounted() {
    this.getCommands();
  }
};
</script>

<style scoped>
/* Commands page styling */
.commands-section {
  width: 100%;
  max-width: var(--container-max-width);
  margin: 0 auto;
  text-align: center;
}

/* Header area */
.commands-header {
  margin-bottom: var(--spacing-xl);
  text-align: center;
}

.commands-title {
  font-size: clamp(2.5rem, 6vw, var(--font-size-display));
  font-weight: bold;
  margin-bottom: var(--spacing-sm);
  background: linear-gradient(135deg, #fe5ea1, #00b7ff);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.commands-subtitle {
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  margin: 0 auto var(--spacing-lg) auto;
  text-align: center;
  max-width: none;
}

/* Search container */
.search-container {
  position: relative;
  max-width: 500px;
  margin: 0 auto;
}

.search-input {
  width: 100%;
  padding: var(--spacing-md) var(--spacing-md) var(--spacing-md) 3.5rem;
  border-radius: var(--border-radius-xl);
  border: 2px solid transparent;
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  font-family: var(--font-primary);
  transition: all var(--transition-normal);
  box-shadow: var(--shadow-md);
}

.search-input::placeholder {
  color: var(--color-text-muted);
}

.search-input:focus {
  outline: none;
  border-color: var(--color-accent-discord);
  box-shadow: var(--shadow-lg), 0 0 20px rgba(88, 101, 242, 0.2);
}

.search-icon {
  position: absolute;
  left: 1.25rem;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: var(--color-text-muted);
  pointer-events: none;
  transition: color var(--transition-normal);
}

.search-input:focus ~ .search-icon,
.search-container:focus-within .search-icon {
  color: var(--color-accent-discord);
}

.search-clear {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: var(--color-text-muted);
  transition: color var(--transition-normal);
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-clear:hover {
  color: var(--color-text-primary);
}

.search-clear svg {
  width: 18px;
  height: 18px;
}

/* Category buttons */
.category-buttons {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-xl);
}

.category-button {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius-xl);
  border: 2px solid transparent;
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
  box-shadow: var(--shadow-sm);
  backdrop-filter: blur(10px);
}

.category-button:hover {
  background: var(--color-bg-secondary);
  border-color: var(--color-accent-discord);
  color: var(--color-text-primary);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.category-button.selected {
  background: var(--color-accent-discord);
  color: var(--color-text-primary);
  border-color: var(--color-accent-discord);
  transform: translateY(-1px);
  box-shadow: var(--shadow-lg), 0 0 20px rgba(88, 101, 242, 0.3);
}

.category-icon {
  font-size: 1.1em;
}

.category-name {
  font-weight: 600;
}

.category-count {
  background: rgba(255, 255, 255, 0.15);
  padding: 0.15rem 0.5rem;
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-xs);
  font-weight: 700;
  min-width: 1.5rem;
}

.category-button.selected .category-count {
  background: rgba(255, 255, 255, 0.25);
}

/* Results info */
.results-info {
  margin-bottom: var(--spacing-lg);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.results-count {
  font-weight: 600;
  color: var(--color-accent-discord);
}

.results-query {
  color: var(--color-text-muted);
}

/* Commands grid */
.commands-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(380px, 100%), 1fr));
  gap: var(--spacing-lg);
  margin: 0 auto;
  max-width: 1200px;
  padding-bottom: var(--spacing-xxl);
}

/* Command card */
.command-card {
  position: relative;
  background: linear-gradient(145deg, var(--color-bg-tertiary) 0%, var(--color-bg-secondary) 100%);
  border-radius: var(--border-radius-xl);
  padding: var(--spacing-lg);
  box-shadow: var(--shadow-md);
  transition: all var(--transition-normal);
  cursor: pointer;
  overflow: hidden;
  text-align: left;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.command-card:nth-child(-n+12) {
  animation: cardAppear 0.4s ease-out backwards;
}

.command-card:nth-child(1) { animation-delay: 0s; }
.command-card:nth-child(2) { animation-delay: 0.03s; }
.command-card:nth-child(3) { animation-delay: 0.06s; }
.command-card:nth-child(4) { animation-delay: 0.09s; }
.command-card:nth-child(5) { animation-delay: 0.12s; }
.command-card:nth-child(6) { animation-delay: 0.15s; }
.command-card:nth-child(7) { animation-delay: 0.18s; }
.command-card:nth-child(8) { animation-delay: 0.21s; }
.command-card:nth-child(9) { animation-delay: 0.24s; }
.command-card:nth-child(10) { animation-delay: 0.27s; }
.command-card:nth-child(11) { animation-delay: 0.3s; }
.command-card:nth-child(12) { animation-delay: 0.33s; }

@keyframes cardAppear {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.card-glow {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(88, 101, 242, 0.15) 0%, transparent 60%);
  opacity: 0;
  transition: opacity var(--transition-normal);
  pointer-events: none;
}

.command-card:hover .card-glow,
.command-card.hovered .card-glow {
  opacity: 1;
}

.command-card:hover,
.command-card.hovered {
  transform: translateY(-6px) scale(1.02);
  box-shadow: var(--shadow-xl), 0 0 30px rgba(88, 101, 242, 0.15);
  border-color: rgba(88, 101, 242, 0.3);
}

.card-content {
  position: relative;
  z-index: 1;
}

.command-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-sm);
  flex-wrap: wrap;
  gap: var(--spacing-xs);
}

.command-name {
  font-size: var(--font-size-lg);
  font-weight: bold;
  color: var(--color-text-primary);
  background: var(--color-gradient-secondary);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
}

.command-category-tag {
  font-size: var(--font-size-xs);
  padding: 0.2rem 0.6rem;
  background: rgba(88, 101, 242, 0.2);
  color: var(--color-accent-discord);
  border-radius: var(--border-radius-md);
  font-weight: 600;
}

.command-description {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: 0 0 var(--spacing-md) 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.command-footer {
  display: flex;
  justify-content: flex-end;
}

.view-details {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: var(--font-size-xs);
  color: var(--color-accent-discord);
  font-weight: 600;
  opacity: 0;
  transform: translateX(-10px);
  transition: all var(--transition-normal);
}

.view-details svg {
  width: 16px;
  height: 16px;
  transition: transform var(--transition-normal);
}

.command-card:hover .view-details,
.command-card.hovered .view-details {
  opacity: 1;
  transform: translateX(0);
}

.command-card:hover .view-details svg,
.command-card.hovered .view-details svg {
  transform: translateX(3px);
}

/* No results */
.no-results {
  text-align: center;
  padding: var(--spacing-xxl);
  color: var(--color-text-muted);
}

.no-results svg {
  width: 64px;
  height: 64px;
  margin-bottom: var(--spacing-md);
  opacity: 0.5;
}

.no-results p {
  font-size: var(--font-size-md);
  margin-bottom: var(--spacing-lg);
}

.clear-search-btn {
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius-lg);
  border: none;
  background: var(--color-accent-discord);
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
}

.clear-search-btn:hover {
  background: var(--color-accent-discord-hover);
  transform: translateY(-2px);
}

/* Responsive design */
@media (max-width: 768px) {
  .commands-grid {
    grid-template-columns: 1fr;
    gap: var(--spacing-md);
  }
  
  .category-buttons {
    gap: var(--spacing-sm);
  }
  
  .category-button {
    padding: var(--spacing-xs) var(--spacing-md);
    font-size: var(--font-size-xs);
  }
  
  .category-icon {
    font-size: 1em;
  }
  
  .command-card {
    padding: var(--spacing-md);
  }

  .search-container {
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .category-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .category-button {
    width: 100%;
    max-width: 220px;
    justify-content: center;
  }
  
  .command-name {
    font-size: var(--font-size-md);
  }
  
  .command-description {
    font-size: var(--font-size-xs);
  }

  .commands-subtitle {
    font-size: var(--font-size-sm);
  }
}

/* Focus states for accessibility */
.category-button:focus-visible,
.command-card:focus-visible,
.search-input:focus-visible {
  outline: 2px solid var(--color-accent-discord);
  outline-offset: 2px;
}
</style>